import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanLoad, Route, Router, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { StoreService } from 'app/ngrx/store.service';
import { hasPagePermission } from 'app/shared/utils/profile-permissions.util';
import { resolveSicaRoutePath, SICA_ROUTES, isSuperAdminOnlySicaRoute } from 'app/shared/utils/sica-route-catalog';

/** Page authorization uses only the connected user's grants and the common-route catalog. */
@Injectable({ providedIn: 'root' })
export class RoleGuard implements CanActivate, CanActivateChild, CanLoad {
    constructor(private readonly router: Router, private readonly storeService: StoreService) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        return this.checkPermission(state.url);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        return this.checkPermission(state.url);
    }

    canLoad(route: Route, segments: UrlSegment[]): boolean {
        return this.checkPermission('/' + segments.map(segment => segment.path).join('/'));
    }

    private checkPermission(url: string): boolean {
        const parsed = this.router.parseUrl(url);
        const segments = parsed.root.children.primary?.segments.map(segment => segment.path) || [];
        const route = resolveSicaRoutePath(segments);
        const user = this.storeService.getConnectedUser();
        if (hasPagePermission(user, route)) return true;
        console.warn('[Access denied] RoleGuard', {
            requestedUrl: url,
            requiredLink: route,
            component: SICA_ROUTES.find(item => item.path == route),
            reason: isSuperAdminOnlySicaRoute(route)
                ? 'This page requires SuperAdmin access.'
                : 'No matching page permission in the connected user profiles.',
            profiles: (user?.user_UserProfiles || []).map(item => ({
                id: item.userProfileId,
                name: item.userProfile?.name,
                permissions: (item.userProfile?.permissions || []).map(permission => ({
                    id: permission.id,
                    link: permission.pageRoute,
                    accessLevel: permission.accessLevel,
                    userProfileId: permission.userProfileId
                }))
            }))
        });
        this.router.navigateByUrl('/forbidden-access');
        return false;
    }
}
